import type { RosterAppointment } from "@/app/api/appointment/get/roster/route";
import {
  type Consumer,
  InsurancesStatusEnum,
  type Maybe,
  ReviewStatusEnum,
  type RosterSlot,
} from "@frontend/lib/graphql/generated-gql";
import type {
  AppointmentSchema,
  SlotsSchema,
} from "@frontend/lib/schemas/appointmentSchema";

export enum QueryRoster {
  UPCOMING = "upcoming",
  ACTIVE = "active",
  MISSED = "missed",
  UNSCHEDULED = "unscheduled",
}

export enum QueryTab {
  UNSCHEDULED = "unscheduled",
  UPCOMING = "upcoming",
  PAST = "past",
}

export enum QueryAction {
  MISSING_PRIMARY_INSURANCE = "missing-primary-insurance",
  UPDATE_INSURANCE = "update-insurance",
  VERIFY_INSURANCE = "verify-insurance",
  PAYMENT = "payment",
  CREATE_PRICES = "create-prices",
  VIEW_PATIENT = "view-patient",
  INTAKE = "intake",
}

export enum QueryFrom {
  DOTS = "dots",
  PATIENT = "patient",
}

export type ConsumerHref = {
  pathname: string;
  query: {
    roster?: string;
    tab?: string;
    action?: QueryAction;
    from?: QueryFrom;
  };
};

// ranked from best to worst
export const statusRanking = [
  InsurancesStatusEnum.Active,
  InsurancesStatusEnum.Manuallyverified,
  InsurancesStatusEnum.ManuallyVerifiedExpired,
  InsurancesStatusEnum.ActiveBenefitsNotReturned,
  InsurancesStatusEnum.ActiveOutOfNetwork,
  InsurancesStatusEnum.Unverified,
  InsurancesStatusEnum.Inactive,
];

export const repriceStatusRanking = [
  ReviewStatusEnum.RepriceInProgress,
  ReviewStatusEnum.NeedsAdminCreated,
  ReviewStatusEnum.ConfirmSelfpay,
  ReviewStatusEnum.ConfirmPrimaryOnly,
  ReviewStatusEnum.ConfirmContract,
  ReviewStatusEnum.InsuredPricingFailed,
  undefined,
];

export type AdminFlagDetails = {
  flag: boolean;
  dots: boolean;
  text: SecondDotText;
  href: ConsumerHref;
};

export const priorityToFieldMap = {
  1: "primaryInsurance",
  2: "secondaryInsurance",
  3: "tertiaryInsurance",
} as Record<
  number,
  "primaryInsurance" | "secondaryInsurance" | "tertiaryInsurance"
>;

export const fieldToPriorityMap = {
  primaryInsurance: 1,
  secondaryInsurance: 2,
  tertiaryInsurance: 3,
};

export type InsuranceWithPriority = {
  priority?: Maybe<number>;
};

export const getInsurancesByPriority = <T extends InsuranceWithPriority>(
  insurances: (T | null | undefined)[],
) => {
  const findInsurance = (priority: number) =>
    insurances.find((insurance) => insurance?.priority === priority);

  const primary = findInsurance(1);
  const secondary = findInsurance(2);
  const tertiary = findInsurance(3);

  return [primary, secondary, tertiary];
};

export const isMissingPrimaryInsurance = <T extends InsuranceWithPriority>(
  insurances: (T | null | undefined)[],
) => {
  const [primary, secondary, tertiary] = getInsurancesByPriority(insurances);

  if (!primary) {
    return !!secondary || !!tertiary;
  }

  return false;
};

export const isMissingSecondaryInsurance = <T extends InsuranceWithPriority>(
  insurances: (T | null | undefined)[],
) => {
  const [_, secondary, tertiary] = getInsurancesByPriority(insurances);

  if (!secondary) {
    return !!tertiary;
  }

  return false;
};

export const isMissingInsurance = <
  T extends { priority?: Maybe<number> | undefined },
>(
  insurances: (T | null | undefined)[],
) => {
  return (
    isMissingPrimaryInsurance(insurances) ||
    isMissingSecondaryInsurance(insurances)
  );
};

export const isBadInsuranceStatus = (insuranceStatus: InsurancesStatusEnum) => {
  return [
    InsurancesStatusEnum.Unverified,
    InsurancesStatusEnum.Inactive,
    InsurancesStatusEnum.ActiveBenefitsNotReturned,
    InsurancesStatusEnum.ManuallyVerifiedExpired,
  ].includes(insuranceStatus);
};

const getWorseInsuranceStatusAndType = (consumer: Consumer): WorseStatus => {
  const [primary, secondary] = getInsurancesByPriority(consumer.insurances);

  const primaryInsuranceStatus = primary?.status;
  const secondaryInsuranceStatus = secondary?.status;

  if (!primaryInsuranceStatus || !secondaryInsuranceStatus) {
    if (primaryInsuranceStatus) {
      return { status: primaryInsuranceStatus, type: "primary" };
    }

    if (secondaryInsuranceStatus) {
      return { status: secondaryInsuranceStatus, type: "secondary" };
    }

    return null;
  }

  return statusRanking.indexOf(primaryInsuranceStatus) >
    statusRanking.indexOf(secondaryInsuranceStatus)
    ? { status: primaryInsuranceStatus, type: "primary" }
    : { status: secondaryInsuranceStatus, type: "secondary" };
};

export const getBoughtTreatmentFlag = (
  isGlobalRosterCard: boolean,
  appointment?: RosterAppointment | AppointmentSchema,
) => {
  const boughtTreatments = getBoughtTreatmentsFromAppointment(
    isGlobalRosterCard,
    appointment,
  );

  const status = boughtTreatments.reduce((acc, boughtTreatment) => {
    const index = repriceStatusRanking.indexOf(
      boughtTreatment.reviewStatus ?? undefined,
    );
    return index > acc ? index : acc;
  }, 0);

  return repriceStatusRanking[status];
};

export const getBoughtTreatmentsFromAppointment = (
  isGlobalRosterCard: boolean,
  appointment?: RosterAppointment | AppointmentSchema,
) => {
  if (!appointment) {
    return [];
  }

  return isGlobalRosterCard
    ? (appointment?.slots as RosterSlot[]).flatMap(
        (slot) => slot.rosterBoughtTreatments,
      )
    : (appointment?.slots as SlotsSchema[]).flatMap((slot) => slot.procedures);
};

type WorseStatus = {
  status: InsurancesStatusEnum;
  type: "primary" | "secondary";
} | null;

export enum SecondDotText {
  UpdateInsurance = "Update Insurance",
  VerifyInsurance = "Verify Insurance",
  ReverifyInsurance = "Re-verify Insurance",
  CreatePrices = "Create Prices",
  ConfirmPrices = "Confirm Prices",
  CollectPayment = "Collect Payment",
  RepriceInProgress = "Reprice in progress",
  MissingPrimaryInsurance = "Missing Primary Insurance",
}

export const getNavigateToConsumerObject = ({
  consumerId,
  appointmentId,
  roster,
  tab,
  from,
  action,
}: {
  consumerId: string;
  appointmentId?: string;
  roster?: string;
  tab?: string;
  from?: QueryFrom;
  action?: QueryAction;
}) => {
  return {
    pathname: `/roster/consumer/${consumerId}`,
    query: {
      ...(appointmentId && { appointmentId }),
      ...(roster && { roster }),
      ...(tab && { tab }),
      ...(from && { from }),
      ...(action && { action }),
    },
  };
};

export const getNavigateToConsumerString = ({
  consumerId,
  appointmentId,
  roster,
  tab,
  from,
  action,
}: {
  consumerId: string;
  appointmentId?: string;
  roster?: string;
  tab?: string;
  from?: QueryFrom;
  action?: QueryAction;
}) => {
  const href = getNavigateToConsumerObject({
    consumerId,
    appointmentId,
    roster,
    tab,
    from,
    action,
  });

  return `${href.pathname}?${new URLSearchParams(href.query).toString()}`;
};

const isConfirmFlagValid = (
  flag: ReviewStatusEnum,
  type: "primary" | "secondary",
) => {
  if (type === "primary") {
    return flag === ReviewStatusEnum.ConfirmSelfpay;
  }

  return (
    flag === ReviewStatusEnum.ConfirmSelfpay ||
    flag === ReviewStatusEnum.ConfirmPrimaryOnly
  );
};

const isCreateFlagValid = (flag: ReviewStatusEnum) => {
  return flag === ReviewStatusEnum.NeedsAdminCreated;
};

const isRepriceFlagValid = (flag: ReviewStatusEnum) => {
  return flag === ReviewStatusEnum.RepriceInProgress;
};

export const getAdminFlagDetails = (
  searchParams: URLSearchParams,
  isGlobalRosterCard: boolean,
  consumer: Consumer,
  appointment?: RosterAppointment | AppointmentSchema,
) => {
  const flag = getBoughtTreatmentFlag(isGlobalRosterCard, appointment);
  const worseStatus = getWorseInsuranceStatusAndType(consumer);

  const roster = searchParams.get("roster") ?? undefined;
  const tab = searchParams.get("tab") ?? undefined;

  const handleUpdateUrl = (action: QueryAction) => {
    return getNavigateToConsumerObject({
      consumerId: consumer.id,
      roster,
      tab,
      from: QueryFrom.DOTS,
      action,
    });
  };

  const handleVerifyInsurance = () => {
    return handleUpdateUrl(QueryAction.VERIFY_INSURANCE);
  };

  const handleUpdateInsurance = () => {
    return handleUpdateUrl(QueryAction.UPDATE_INSURANCE);
  };

  const handleMissingPrimaryInsurance = () => {
    return handleUpdateUrl(QueryAction.MISSING_PRIMARY_INSURANCE);
  };

  const defaultAction = {
    flag: false,
    dots: true,
    text: SecondDotText.CollectPayment,
    href: getNavigateToConsumerObject({
      consumerId: consumer.id,
      roster: searchParams.get("roster") ?? undefined,
      tab: searchParams.get("tab") ?? undefined,
      from: QueryFrom.DOTS,
      action: QueryAction.PAYMENT,
    }),
  };

  const handleCreatePrices = () => {
    return defaultAction.href;
  };

  const handleConfirmPrices = () => {
    return defaultAction.href;
  };

  if (!worseStatus || !flag) {
    return defaultAction;
  }

  const { status, type } = worseStatus;

  if (isMissingPrimaryInsurance(consumer.insurances)) {
    if (isConfirmFlagValid(flag, type)) {
      return isGlobalRosterCard
        ? {
            flag: true,
            dots: false,
            text: SecondDotText.MissingPrimaryInsurance,
            href: handleMissingPrimaryInsurance(),
          }
        : {
            flag: true,
            dots: true,
            text: SecondDotText.ConfirmPrices,
            href: handleConfirmPrices(),
          };
    }
  }

  switch (status) {
    case InsurancesStatusEnum.Inactive:
      if (isRepriceFlagValid(flag)) {
        return {
          flag: true,
          dots: false,
          text: SecondDotText.RepriceInProgress,
          href: undefined,
        };
      }
      if (isConfirmFlagValid(flag, type)) {
        return isGlobalRosterCard
          ? {
              flag: true,
              dots: false,
              text: SecondDotText.UpdateInsurance,
              href: handleUpdateInsurance(),
            }
          : {
              flag: true,
              dots: true,
              text: SecondDotText.ConfirmPrices,
              href: handleConfirmPrices(),
            };
      }
      break;

    case InsurancesStatusEnum.Unverified:
      if (isCreateFlagValid(flag)) {
        return isGlobalRosterCard
          ? {
              flag: true,
              dots: false,
              text: SecondDotText.VerifyInsurance,
              href: handleVerifyInsurance(),
            }
          : {
              flag: true,
              dots: true,
              text: SecondDotText.CreatePrices,
              href: handleCreatePrices(),
            };
      }
      break;

    case InsurancesStatusEnum.ManuallyVerifiedExpired:
      if (isCreateFlagValid(flag)) {
        return isGlobalRosterCard
          ? {
              flag: true,
              dots: false,
              text: SecondDotText.ReverifyInsurance,
              href: handleVerifyInsurance(),
            }
          : {
              flag: true,
              dots: true,
              text: SecondDotText.CreatePrices,
              href: handleCreatePrices(),
            };
      }

      break;

    case InsurancesStatusEnum.ActiveBenefitsNotReturned:
      if (isCreateFlagValid(flag)) {
        return isGlobalRosterCard
          ? {
              flag: true,
              dots: false,
              text: SecondDotText.VerifyInsurance,
              href: handleVerifyInsurance(),
            }
          : {
              flag: true,
              dots: true,
              text: SecondDotText.CreatePrices,
              href: handleCreatePrices(),
            };
      }
      break;

    case InsurancesStatusEnum.ActiveOutOfNetwork:
      if (isRepriceFlagValid(flag)) {
        return {
          flag: true,
          dots: false,
          text: SecondDotText.RepriceInProgress,
          href: undefined,
        };
      }
      if (isConfirmFlagValid(flag, type)) {
        return {
          flag: true,
          dots: true,
          text: SecondDotText.ConfirmPrices,
          href: handleConfirmPrices(),
        };
      }
      break;

    case InsurancesStatusEnum.Manuallyverified:
      if (isCreateFlagValid(flag)) {
        return {
          flag: true,
          dots: true,
          text: SecondDotText.CreatePrices,
          href: handleCreatePrices(),
        };
      }
      break;

    case InsurancesStatusEnum.Active:
      if (isRepriceFlagValid(flag)) {
        return {
          flag: true,
          dots: false,
          text: SecondDotText.RepriceInProgress,
          href: undefined,
        };
      }
      break;

    default:
      return defaultAction;
  }

  return defaultAction;
};
